import RadioGroup from "@mui/material/RadioGroup";
import React, {
  ChangeEvent,
  KeyboardEvent,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormControlLabel, Radio } from "@mui/material";
import { getCodeData } from "../utils/CommonUtils";
import { StyleSettings } from "../../styles/StyleSettings";

interface SnRadioBoxProps {
  defaultValue?: string;
  code?: string;
  sql?: string;
  emptyLine?: boolean;
  onValueChanged?: (value: string) => void;
  isReadOnly?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  js_combo?: any[] | string;
}

const SnRadioBox = React.memo(
  ({
    defaultValue,
    code,
    sql,
    emptyLine,
    onValueChanged,
    isReadOnly = false,
    inputRef,
    isSearchPanel = false,
    onFocus,
    js_combo,
  }: SnRadioBoxProps) => {
    const [value, setValue] = useState<string | undefined>(defaultValue);
    const [options, setOptions] = useState<any[]>([]);
    const internalRadioGroupRef = useRef<HTMLDivElement | null>(null);

    const fetchDefaultValue = async () => {
      try {
        if (defaultValue !== undefined) {
          setValue(defaultValue);
        }
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const dataSource = Array.isArray(js_combo)
            ? js_combo
            : typeof js_combo === "string"
              ? JSON.parse(js_combo)
              : [];
          const data =
            dataSource.length > 0
              ? dataSource
              : await getCodeData(code || "", sql || "", emptyLine);

          // const data = await getCodeData(code || "", sql || "", emptyLine);
          setOptions(Array.isArray(data) ? data : data ? [data] : []);
        } catch (error) {}
      };
      fetchData();
    }, [code, sql, emptyLine]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setValue(inputValue);
      if (onValueChanged) {
        onValueChanged(inputValue);
      }
    };

    // RadioGroup이 focus를 받았을 때 선택된 라디오 버튼으로 focus 이동
    const handleRadioGroupFocus = () => {
      setTimeout(() => {
        const radioInputs = internalRadioGroupRef.current?.querySelectorAll(
          'input[type="radio"]'
        );
        if (!radioInputs) return;

        const selectedIndex = options.findIndex(
          (option) => option.code === value
        );

        // 선택된 값이 없으면 첫 번째로 fallback
        const targetIndex = selectedIndex >= 0 ? selectedIndex : 0;
        const targetInput = radioInputs[targetIndex] as HTMLElement;

        targetInput?.focus();
      }, 0);
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (!isSearchPanel) {
          e.preventDefault();
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    return (
      <div>
        <RadioGroup
          ref={(node: HTMLDivElement | null) => {
            // 외부에서 전달된 ref 설정
            if (inputRef) {
              if (typeof inputRef === "function") {
                inputRef(node);
              } else if (inputRef.current !== undefined) {
                inputRef.current = node;
              }
            }
            // 내부 ref 설정
            internalRadioGroupRef.current = node;
          }}
          row
          tabIndex={0}
          value={value}
          onChange={handleChange}
          onFocus={handleRadioGroupFocus}
          sx={{
            height: StyleSettings.control_height,
            display: "flex",
            alignItems: "center",
            marginLeft: "12px",
            "&:focus-within": {
              outline: "none",
            },
            "&:focus": {
              outline: "none",
            },
          }}
        >
          {options.map((option, i) => (
            <FormControlLabel
              key={option.code}
              value={option.code}
              control={
                <Radio
                  onKeyDown={handleKeyDown}
                  sx={{
                    padding: "0 4px",
                    "& .MuiSvgIcon-root": {
                      fontSize: 20, // 라디오 버튼 크기 조절
                    },
                  }}
                />
              }
              label={option.name}
              disabled={isReadOnly}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
              slotProps={{
                // label 스타일 적용
                typography: {
                  style: {
                    height: "100%",
                    fontSize: StyleSettings.font,
                    display: "flex",
                    alignItems: "center",
                  },
                },
              }}
            />
          ))}
        </RadioGroup>
      </div>
    );
  }
);

export default SnRadioBox;
